'use client'

import { toast } from '@/components/common/toast'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { useProductVouchers } from '@/hooks/react-query/products/use-product'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { cn } from '@/lib/utils'
import { CAMPAIGN_NAMES } from '@/lib/utils/campaign/campaign-names'
import { STORAGE_KEYS } from '@/lib/constants/storage-keys'
import { SessionStorage } from '@/lib/utils/session-storage'
import { useCampaignActive } from '@/providers/campaign-provider'
import { __cAccountService } from '@/services/client/__c.account.service'
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

interface VoucherItemProps {
  discount: string
  description?: string
  code: string
  onSave?: () => void
  quantity?: number
  isPending?: boolean
  isSaved?: boolean
}

const SAVED_LABEL_DURATION = 2000

export const ProductInfoVouchers = ({ productId }: { productId: string }) => {
  const { data: vouchers } = useProductVouchers(productId)
  const t = useTranslations()
  const isLoggedIn = useIsLoggedIn()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // Mã vừa lưu thành công -> hiển thị "Đã lưu" trong 2s
  const [savedCode, setSavedCode] = useState<string | null>(null)
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => () => clearTimeout(savedTimeoutRef.current ?? undefined), [])

  const markVoucherSaved = (code: string) => {
    clearTimeout(savedTimeoutRef.current ?? undefined)
    setSavedCode(code)
    savedTimeoutRef.current = setTimeout(
      () => setSavedCode(null),
      SAVED_LABEL_DURATION
    )
  }

  const { mutate: saveMutation, isPending } = useMutation({
    mutationFn: (voucher: VoucherItemProps) =>
      __cAccountService.saveVoucher(voucher.code),
    onError: (error) => {
      toast.message({
        title: t('title.voucher'),
        message: error.message || t('error.default'),
      })
    },
    onSuccess: (data, voucher) => {
      if (data.status === 'success') {
        markVoucherSaved(voucher.code)
        toast.message({
          title: t('product_detail.notification.save_voucher_success', {
            code: voucher.code,
            discount: voucher.discount,
          }),
          message: data.message,
        })
      }
    },
  })
  const isCampaignActive = useCampaignActive(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)

  if (!vouchers || vouchers.length === 0) return null

  const handleSaveVoucher = (voucher: VoucherItemProps) => {
    if (!voucher.code) return
    if (isLoggedIn) {
      saveMutation(voucher)
    } else {
      const saved = SessionStorage.set(
        STORAGE_KEYS.PENDING_VOUCHER,
        voucher.code
      )
      if (saved) {
        markVoucherSaved(voucher.code)
        toast.message({
          title: 'Mã giảm giá đã được lưu tạm',
          message:
            'Đăng nhập để lưu mã vào ví hoặc sử dụng ngay trong giỏ hàng',
        })
      }
    }
  }

  if (isCampaignActive) return null

  const [firstVoucher] = vouchers

  return (
    <section
      className='py-2 lg:py-0'
      aria-label='Vouchers Section Product Detail'
    >
      <div className='flex flex-col rounded-lg bg-neutral-100 px-4 py-2'>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex min-w-0 items-center gap-2'>
            <Image
              src='/icons/product/icon-ticket-percent.svg'
              alt=''
              width={24}
              height={24}
              className='shrink-0'
            />
            <p className='truncate text-base leading-6 text-black'>
              {t.rich('product_detail.title.voucher_summary', {
                discount: firstVoucher.discount,
                code: firstVoucher.code,
                strong: (chunks) => <span className='font-bold'>{chunks}</span>,
              })}
            </p>
          </div>
          {firstVoucher.description && (
            <Tooltip
              content={
                <p className='max-w-[280px] text-xs text-neutral-900'>
                  {firstVoucher.description}
                </p>
              }
            >
              <button
                type='button'
                className='shrink-0'
                aria-label={firstVoucher.description}
              >
                <Image
                  src='/icons/product/icon-info-outline.svg'
                  alt=''
                  width={16}
                  height={16}
                />
              </button>
            </Tooltip>
          )}
        </div>
        <div className='flex items-center justify-end gap-4'>
          <button
            type='button'
            onClick={() => handleSaveVoucher(firstVoucher)}
            disabled={isPending}
            className='flex items-center gap-1 py-1 pl-2 text-sm leading-5 text-primary-500 disabled:opacity-50'
            ga-tracking-value='product-detail__voucher__save'
            ga-tracking-label='product-detail__voucher__save'
          >
            <Image
              src='/icons/product/icon-bookmark-outline.svg'
              alt=''
              width={11.4}
              height={12}
            />
            {isPending
              ? t('product_detail.button.saving')
              : savedCode === firstVoucher.code
                ? t('product_detail.button.saved')
                : t('product_detail.button.save')}
          </button>
          {vouchers.length > 1 && (
            <button
              type='button'
              onClick={() => setIsDialogOpen(true)}
              className='flex items-center gap-1 py-1 text-sm text-primary-500 underline'
              ga-tracking-value='product-detail__voucher__view-more'
              ga-tracking-label='product-detail__voucher__view-more'
            >
              {t('product_detail.title.voucher_view_more', {
                count: vouchers.length,
              })}
              <Image
                src='/icons/product/icon-arrow-right-primary.svg'
                alt=''
                width={11.4}
                height={12}
              />
            </button>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className={cn(
            'flex flex-col gap-6 bg-light',
            // Mobile: bottom sheet full width
            'bottom-0 top-auto w-full max-w-none translate-y-0 rounded-b-none rounded-t-2xl px-4 pb-8 pt-10 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom sm:rounded-b-none sm:rounded-t-2xl',
            // Desktop: popup giữa màn hình theo Figma
            'lg:bottom-auto lg:top-[50%] lg:max-w-[700px] lg:translate-y-[-50%] lg:rounded-2xl lg:px-9 lg:py-[52px] lg:data-[state=closed]:slide-out-to-top-[48%] lg:data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <DialogClose
            className='absolute -top-[21px] right-4 size-[50px] lg:-right-[25px]'
            aria-label='Close'
          >
            <Image src='/icons/product/icon-close-circle-bg.svg' alt='' fill />
            <Image
              src='/icons/product/icon-close-x-white.svg'
              alt=''
              width={12.6}
              height={17}
              className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
            />
          </DialogClose>
          <DialogTitle className='font-criteria text-2xl font-semibold leading-[35px] tracking-[-0.02em] text-black'>
            {t('product_detail.title.voucher_available')}
          </DialogTitle>
          <Carousel
            opts={{ align: 'start', dragFree: true }}
            aria-label='Vouchers'
            className={cn(isPending && 'opacity-50')}
          >
            <CarouselContent className='-ml-2'>
              {vouchers.map((voucher, index) => (
                <CarouselItem
                  key={`voucher-${voucher.code}-${index}`}
                  className='basis-auto cursor-default pl-2'
                >
                  <ProductInfoVoucherItem
                    discount={voucher.discount}
                    description={voucher.description}
                    code={voucher.code}
                    onSave={() => handleSaveVoucher(voucher)}
                    isPending={isPending}
                    isSaved={savedCode === voucher.code}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </DialogContent>
      </Dialog>
    </section>
  )
}

// Số lượt còn lại đang fake ở FE: 5-10 lượt, cố định theo mã để SSR/CSR khớp nhau
const getFakeRemainingQuantity = (code: string) => {
  const hash = [...code].reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return 5 + (hash % 6)
}

export const ProductInfoVoucherItem = ({
  discount,
  description,
  onSave,
  code,
  isPending,
  isSaved,
}: VoucherItemProps) => {
  const t = useTranslations('product_detail')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      toast.message({ title: t('title.voucher_copied', { code }) })
    } catch (error) {
      console.error('Error copying voucher code:', error)
    }
  }

  return (
    <div className='relative flex h-full w-[180px] overflow-hidden rounded'>
      <div className='absolute inset-y-0 left-0 right-[25.7%]'>
        <Image
          src='/icons/product/voucher-coupon-bg.svg'
          alt=''
          fill
          unoptimized
        />
      </div>
      <div className='relative flex w-7 shrink-0 items-center justify-center'>
        <p className='-rotate-90 whitespace-nowrap font-criteria text-sm font-semibold leading-5 tracking-[-0.02em] text-white'>
          {discount} OFF
        </p>
      </div>
      <div className='relative flex min-w-0 flex-1 flex-col items-end justify-between gap-1 rounded-r-[10px] border-y border-r border-neutral-200 bg-light p-2'>
        <div className='flex w-full flex-col'>
          <div className='flex w-full items-center justify-between gap-1'>
            <div className='flex min-w-0 items-center gap-1 whitespace-nowrap'>
              <p
                title={code}
                className='truncate font-criteria text-xs font-bold leading-4 tracking-[-0.02em] text-neutral-900'
              >
                {code}
              </p>
              <p className='text-[10px] text-neutral-500'>
                {t('title.voucher_left', {
                  quantity: getFakeRemainingQuantity(code),
                })}
              </p>
            </div>
            <button
              type='button'
              onClick={handleCopy}
              className='shrink-0'
              aria-label={t('title.voucher_copied', { code })}
              ga-tracking-value='product-detail__voucher__copy'
              ga-tracking-label='product-detail__voucher__copy'
            >
              <Image
                src='/icons/product/icon-copy-outline.svg'
                alt=''
                width={12}
                height={12}
              />
            </button>
          </div>
          {description && (
            <p className='line-clamp-2 text-[10px] text-neutral-600'>
              {description}
            </p>
          )}
        </div>
        <button
          type='button'
          onClick={() => onSave?.()}
          disabled={isPending}
          className='rounded-full border border-neutral-900 px-2 pb-1 pt-0.5 text-xs leading-4 text-neutral-900 transition-colors hover:bg-neutral-900 hover:text-light disabled:opacity-50'
          ga-tracking-value='product-detail__voucher__save'
          ga-tracking-label='product-detail__voucher__save'
        >
          {isPending
            ? t('button.saving')
            : isSaved
              ? t('button.saved')
              : t('button.save')}
        </button>
      </div>
    </div>
  )
}
